// src/components/rpa/agent/PBCLetterCard.js
import React, { useState } from 'react';
import axios from 'axios';
import './PBCLetterCard.css';

const API_URL = process.env.REACT_APP_API || 'http://localhost:8000';

const CATEGORY_COLORS = {
  'Financial Statements':    '#3b82f6',
  'Cash & Banking':          '#059669',
  'External Confirmations':  '#7c3aed',
  'Inventory':               '#d97706',
  'Supporting Documentation':'#6b7280',
};

const PBCLetterCard = ({ docId, docName, clientName: initialClientName, pendingItems }) => {
  const [selected,          setSelected]          = useState(() => new Set(pendingItems.map(i => i.stepId)));
  const [clientName,        setClientName]        = useState(initialClientName || '');
  const [clientContact,     setClientContact]     = useState('');
  const [engagementPartner, setEngagementPartner] = useState('Curtis P. Joachim, CPA');
  const [dueDate,           setDueDate]           = useState('');
  const [generating,        setGenerating]        = useState(false);
  const [letter,            setLetter]            = useState('');
  const [error,             setError]             = useState('');
  const [copied,            setCopied]            = useState(false);

  const toggleItem = (stepId) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(stepId) ? next.delete(stepId) : next.add(stepId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === pendingItems.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(pendingItems.map(i => i.stepId)));
    }
  };

  const handleGenerate = async () => {
    const selectedItems = pendingItems.filter(i => selected.has(i.stepId));
    if (!selectedItems.length) { setError('Select at least one PBC item.'); return; }
    setGenerating(true);
    setError('');
    setLetter('');
    try {
      const { data } = await axios.post(
        `${API_URL}/admin/rpa/agent/pbc-letter`,
        { docId, selectedItems, clientName, clientContact, engagementPartner, dueDate },
        { withCredentials: true }
      );
      if (data.ok) setLetter(data.letter);
      else setError(data.error || 'Generation failed.');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(letter);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([letter], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `PBC_Request_${(clientName || 'Client').replace(/\s+/g, '_')}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Group items by category
  const byCategory = {};
  pendingItems.forEach(item => {
    const cat = item.category || 'Other';
    if (!byCategory[cat]) byCategory[cat] = [];
    byCategory[cat].push(item);
  });

  return (
    <div className="pbc-card">
      <div className="pbc-header">
        <span className="pbc-icon">📋</span>
        <div>
          <div className="pbc-title">PBC Document Request</div>
          <div className="pbc-sub">{pendingItems.length} outstanding item{pendingItems.length !== 1 ? 's' : ''} · {docName}</div>
        </div>
      </div>

      {!letter ? (
        <>
          {/* Item selection */}
          <div className="pbc-section-label">
            Select items to include
            <button className="pbc-toggle-all" onClick={toggleAll}>
              {selected.size === pendingItems.length ? 'Deselect All' : 'Select All'}
            </button>
          </div>

          {Object.entries(byCategory).map(([cat, items]) => (
            <div key={cat} className="pbc-category">
              <div className="pbc-category-label" style={{ color: CATEGORY_COLORS[cat] || '#374151' }}>
                {cat}
              </div>
              {items.map(item => (
                <label key={item.stepId} className={`pbc-item ${selected.has(item.stepId) ? 'checked' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selected.has(item.stepId)}
                    onChange={() => toggleItem(item.stepId)}
                  />
                  <div className="pbc-item-body">
                    <div className="pbc-item-title">{item.title}</div>
                    <div className="pbc-item-desc">{item.description}</div>
                    <div className="pbc-item-meta">Format: {item.format} · {item.urgency}</div>
                  </div>
                </label>
              ))}
            </div>
          ))}

          {/* Letter details */}
          <div className="pbc-section-label" style={{ marginTop: 12 }}>Letter details</div>
          <div className="pbc-fields">
            <div className="pbc-field">
              <label>Client Name</label>
              <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Acorn Tech Inc." />
            </div>
            <div className="pbc-field">
              <label>Attention / Contact</label>
              <input value={clientContact} onChange={e => setClientContact(e.target.value)} placeholder="CFO / Controller" />
            </div>
            <div className="pbc-field">
              <label>Engagement Partner</label>
              <input value={engagementPartner} onChange={e => setEngagementPartner(e.target.value)} />
            </div>
            <div className="pbc-field">
              <label>Documents Due By</label>
              <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
            </div>
          </div>

          {error && <div className="pbc-error">{error}</div>}

          <button
            className="pbc-generate-btn"
            onClick={handleGenerate}
            disabled={generating || selected.size === 0}
          >
            {generating ? '⏳ Drafting letter…' : `✉️ Draft Letter (${selected.size} item${selected.size !== 1 ? 's' : ''})`}
          </button>
        </>
      ) : (
        <>
          {/* Rendered letter */}
          <div className="pbc-letter-actions">
            <button className="pbc-action-btn copy" onClick={handleCopy}>
              {copied ? '✓ Copied' : '📋 Copy'}
            </button>
            <button className="pbc-action-btn download" onClick={handleDownload}>
              ⬇ Download
            </button>
            <button className="pbc-action-btn revise" onClick={() => setLetter('')}>
              ✏️ Revise
            </button>
          </div>
          <pre className="pbc-letter-text">{letter}</pre>
        </>
      )}
    </div>
  );
};

export default PBCLetterCard;